import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Share2, Copy, Check, Info } from 'lucide-react';
import { PrimaryButton } from '../components/Button';
import api, { formatApiError } from '../lib/api';

export default function InviteMember() {
    const navigate = useNavigate();
    const [family, setFamily] = useState(null);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/family/me').then((r) => setFamily(r.data.family)).catch(() => {});
    }, []);

    const sendEmail = async () => {
        setError(''); setLoading(true);
        try {
            const { data } = await api.post('/family/invitations', { email });
            setSent({ email: data.invitation_sent_to, link: data.invite_link });
            setEmail('');
        } catch (e) { setError(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const copyLink = async () => {
        const link = `${window.location.origin}/unirte/${family?.code}`;
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const shareLink = async () => {
        const link = `${window.location.origin}/unirte/${family?.code}`;
        const text = `Te invito a unirte a mi familia "${family?.name}" en 4everSync: ${link}`;
        if (navigator.share) {
            try { await navigator.share({ title: '4everSync', text }); } catch {}
        } else {
            await navigator.clipboard.writeText(text);
            alert('Mensaje copiado');
        }
    };

    return (
        <div className="min-h-screen bg-surface app-shell px-6 py-6">
            <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="invite-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <h1 className="text-lg font-bold text-brand-navy">Invitar Miembro</h1>
                <div className="w-10" />
            </div>

            {/* Invitar por correo */}
            <div className="bg-white rounded-2xl shadow-card p-5 mb-4" data-testid="invite-by-email-card">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center"><Mail className="w-5 h-5 text-brand" /></div>
                    <h3 className="text-base font-bold text-brand-navy">Invitar por correo</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">El miembro recibirá un enlace personal y se unirá automáticamente al aceptarlo.</p>
                <input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 bg-white border border-gray-200 rounded-xl px-4 mb-3 focus:border-brand focus:ring-2 focus:ring-brand/20"
                    data-testid="invite-email-input"
                />
                <PrimaryButton onClick={sendEmail} loading={loading} testId="invite-email-send">Enviar invitación</PrimaryButton>
                {sent && (
                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-sm text-emerald-700" data-testid="invite-sent-notice">
                        ✅ Invitación enviada a {sent.email}
                    </div>
                )}
                {error && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700" data-testid="invite-error">{error}</div>}
            </div>

            {/* Compartir enlace */}
            <div className="bg-white rounded-2xl shadow-card p-5 mb-4" data-testid="invite-by-link-card">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center"><Share2 className="w-5 h-5 text-emerald-600" /></div>
                    <h3 className="text-base font-bold text-brand-navy">Compartir enlace</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">Envíalo por WhatsApp, SMS o cualquier app. Quien lo use deberá pedir aprobación.</p>
                <div className="p-3 bg-brand/5 border border-brand/15 rounded-2xl text-center mb-3">
                    <div className="text-xs text-gray-500 mb-1">Código de familia</div>
                    <div className="text-xl font-bold text-brand tracking-wider" data-testid="invite-family-code">{family?.code}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <button onClick={copyLink} className="h-11 bg-white border border-gray-200 rounded-xl font-semibold text-sm text-brand-navy hover:bg-gray-50 flex items-center justify-center gap-2" data-testid="invite-copy-link">
                        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} {copied ? 'Copiado' : 'Copiar'}
                    </button>
                    <button onClick={shareLink} className="h-11 bg-brand text-white rounded-xl font-semibold text-sm hover:bg-brand-dark flex items-center justify-center gap-2" data-testid="invite-share-link">
                        <Share2 className="w-4 h-4" /> Compartir
                    </button>
                </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3" data-testid="invite-tip">
                <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Tip:</strong> Las invitaciones por correo se aceptan automáticamente. Si alguien usa el código compartido, recibirás una solicitud que debes aprobar.
                </p>
            </div>
        </div>
    );
}
